<template>
  <div class="mt-10 border-t border-gray-200 pt-8">
    <div class="flex items-center mb-6">
      <h2 class="text-xl font-bold">
        Clusters
      </h2>

      <reload-button class="ml-auto" @click="reloadGrid" />

      <router-link
        :to="`/data-center/${dataCenterId}/cluster`"
        class="btn-primary ml-4 h-10"
      >
        <PlusCircleIcon class="size-5 -ml-1 mr-2" />
        Add Cluster
      </router-link>
    </div>

    <grid-items-empty v-if="isEmpty && search === null" :link="`/data-center/${dataCenterId}/cluster`" />

    <template v-else>
      <grid
        ref="gridRef"
        :data-handler="getClusters"
        :columns="clusterColumns"
        :row-class="rowClass"
        :per-page="5"
        @enable="onEnableCluster"
        @disable="onDisableCluster"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { apiDomain } from '../composables/useConstants';
import { useToast } from 'vue-toastification';
import { Modal } from '../composables/useModal';
import Grid from '../components/grid/Grid.vue';
import ClusterListActions from '../components/ClusterListActions.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { formatBytes } from '../utils/formatters';

const props = defineProps({
  dataCenterId: {
    type: [ Number, String ],
    required: true
  }
});

defineExpose({
  reloadGrid
});

const DISABLED = '🔴';
const ENABLED = '🟢';

const router = useRouter();
const toast = useToast();
const gridRef = ref(null);
const isEmpty = ref(false);

function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-300/10' : '') + (_row.isEnabled ? '' : '[&>td:not(:last-child)]:!opacity-30');
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/data-center/${props.dataCenterId}/cluster?${searchParams}`;
}

async function getClusters(args) {
  if (!props.dataCenterId) {
    isEmpty.value = true;
    return { total: 0, rows: [] };
  }

  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  return {
    total: data.value.total,
    rows: data.value.clusters
  };
}

const clusterColumns = [
  {
    title: '#',
    prop: 'id',
    class: 'text-xs font-mono',
    sortable: true
  },
  {
    title: 'Name',
    prop: 'name',
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.dataCenterId}/cluster/${row.id}`),
    sortable: true
  },
  {
    title: 'Status',
    value: row => row.isEnabled ? ENABLED : DISABLED,
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.dataCenterId}/cluster/${row.id}`)
  },
  {
    title: 'Primary Gateway',
    value: row => {
      const status = row.isPrimaryGataewayEnabled ? ENABLED : DISABLED;
      return `${status} ${row.primaryGatewayUrl}`;
    },
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.dataCenterId}/cluster/${row.id}`)
  },
  {
    title: 'Reserve Gateway',
    value: row => {
      const status = row.isReserveGataewayEnabled ? ENABLED : DISABLED;
      return `${status} ${row.reserveGatewayUrl}`;
    },
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.dataCenterId}/cluster/${row.id}`)
  },
  {
    title: 'Storage',
    value: row => {
      const status = row.isStorageEnabled ? ENABLED : DISABLED;
      return `${status} ${row.storageUrl}`;
    },
    class: 'cursor-pointer',
    click: row => router.push(`/data-center/${row.dataCenterId}/cluster/${row.id}`)
  },
  {
    title: 'Storage Usage',
    value: row => formatBytes(row.storageUsage),
    class: 'text-xs font-mono',
    sortable: true
  },
  {
    title: 'CreatedAt',
    prop: 'createdAt',
    value: row => new Date(row.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }),
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(ClusterListActions)
  },
  {
    title: 'CreatedAt',
    prop: 'createdAt',
    value: row => new Date(row.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }),
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(ClusterListActions)
  }
];

async function onEnableCluster(row) {
  const confirm = await Modal.confirm({
    title: 'Enable Cluster',
    body: `Enable Cluster "${row.name}"?`,
    okButton: 'Enable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/cluster/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Cluster enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling cluster');
}

async function onDisableCluster(row) {
  const confirm = await Modal.confirm({
    title: 'Disable Cluster',
    body: `Disable Cluster "${row.name}"?`,
    okButton: 'Disable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/cluster/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Cluster disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling cluster');
}
</script>
